import React, { useEffect, useState } from "react";
import { useAuthContext } from "../hooks/useAuthContext";
import Loading from "../utils/Loading";
import { SUBSCRIBERS_BASE_URL } from "../services/SubscribersService";
import { format } from "date-fns";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./SubscribeStyle.css";

const SubscribersList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [approvalChanges, setApprovalChanges] = useState({});
  const { user } = useAuthContext();

  // Fetch all subscribers from the server
  const getAllSubscribers = async () => {
    try {
      const response = await axios.get(
        `${SUBSCRIBERS_BASE_URL}/api/subscribers/list`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      if (Array.isArray(response.data)) {
        setSubscribers(response.data);
      } else {
        setSubscribers([]);
        setError("Unexpected data format received from server.");
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Something went wrong. Unable to fetch subscribers data!"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllSubscribers();
  }, []);

  // Update the approval status of a subscriber in the backend
  const approveASubscriber = async (_id, isApproved) => {
    try {
      const response = await axios.put(
        `${SUBSCRIBERS_BASE_URL}/api/subscribers/update/${_id}`,
        { isApproved },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      Swal.fire({
        title: isApproved ? "Approved!" : "Disapproved!",
        text: `Subscriber has been ${isApproved ? "approved" : "disapproved"}.`,
        icon: "success",
        timer: 1000,
      });

      // Refetch all subscribers to reflect the latest status
      getAllSubscribers();
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Something went wrong. Unable to update approval status!"
      );
    }
  };

  // Handle changes in approval status on radio button selection
  const handleApprovalChange = (_id, isApproved) => {
    setApprovalChanges((prevChanges) => ({
      ...prevChanges,
      [_id]: isApproved,
    }));
  };

  // Submit approval change when "Submit" is clicked
  const handleApprovalSubmit = async (_id) => {
    const isApproved = approvalChanges[_id];
    if (isApproved !== undefined) {
      await approveASubscriber(_id, isApproved);

      // Clean up the change in approvalChanges after submission
      setApprovalChanges((prevChanges) => {
        const updatedChanges = { ...prevChanges };
        delete updatedChanges[_id];
        return updatedChanges;
      });
    }
  };

  // Delete subscriber
  const deleteSubscriber = async (e, _id) => {
    e.preventDefault();
    try {
      const confirmResult = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });
      if (confirmResult.isConfirmed) {
        const response = await axios.delete(
          `${SUBSCRIBERS_BASE_URL}/api/subscribers/delete/${_id}`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );

        setSubscribers(response.data);
        Swal.fire({
          title: "Deleted!",
          text: "Subscriber has been deleted.",
          icon: "success",
          timer: 2000,
        });

        getAllSubscribers();
        navigate("/api/dashboard/subscribers");
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Something went wrong. Unable to delete subscriber!"
      );
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div>{<Loading />}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <section className="subscriber-list mt-4">
      <div className="container">
        <div className="row">
          {subscribers && subscribers.length > 0 && (
            <>
              {" "}
              <div className="col-md-3 col-sm-6 mb-4" id="subscribe-box">
                <h3 className="text-danger">{subscribers.length}</h3>
                <p>Total Subscribers</p>
              </div>{" "}
            </>
          )}
        </div>
        <div className="row">
          <div className="col">
            {subscribers && user.isAdmin && subscribers.length > 0 ? (
              <>
                <h1 className="p-3 bg-white">
                  Subscribers List
                  <hr />
                </h1>
                <div className="table-responsive">
                  <table className="table table-striped table-bordered table-lg table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th scope="col">Email </th>
                        <th scope="col"> Date</th>
                        <th scope="col"> Status</th>
                        <th scope="col">Action</th>
                        <th scope="col">Approve or Disapprove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((subscriber) => (
                        <tr key={subscriber._id}>
                          <td>
                            <i
                              className="fa fa-envelope bg-secondary text-white p-2 rounded-circle"
                              aria-hidden="true"
                            ></i>
                            &nbsp;
                            {subscriber.email}
                          </td>
                          <td className="text-muted created_at">
                            {format(
                              new Date(subscriber.createdAt),
                              "MMMM d, yyyy"
                            )}
                          </td>
                          <td>
                            {subscriber.isApproved ? (
                              <span className="badge bg-success">
                                <i className="fa fa-check"></i> Approved
                              </span>
                            ) : (
                              <span className="badge bg-danger">
                                <i className="fa fa-ban"></i> Unapproved
                              </span>
                            )}
                          </td>
                          <td>
                            {user &&
                              user.isAdmin &&
                              subscriber.isApproved === false && (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={(e) =>
                                    deleteSubscriber(e, subscriber._id)
                                  }
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              )}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="form-check me-3">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name={`isApproved-${subscriber._id}`}
                                  checked={
                                    approvalChanges[subscriber._id] === false
                                  }
                                  onChange={() =>
                                    handleApprovalChange(subscriber._id, false)
                                  }
                                />
                                <label className="form-check-label">NO</label>
                              </div>
                              <div className="form-check me-3">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name={`isApproved-${subscriber._id}`}
                                  checked={
                                    approvalChanges[subscriber._id] === true
                                  }
                                  onChange={() =>
                                    handleApprovalChange(subscriber._id, true)
                                  }
                                />
                                <label className="form-check-label">YES</label>
                              </div>
                              <button
                                className="btn btn-outline-dark btn-sm"
                                onClick={() =>
                                  handleApprovalSubmit(subscriber._id)
                                }
                              >
                                Submit
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="alert alert-warning">
                <h6>No subscribers found!</h6>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SubscribersList;
